import type { FishStockLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import { createId } from '../../utils/id'

export type FishStockSynthesisContext = {
  farmId: string
  productionUnitId: string
}

const MAX_FISH_STOCK = 10_000_000

const parseFishStockValue = (value: unknown): number | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  if (!Number.isInteger(parsed)) return null
  if (parsed < 0 || parsed > MAX_FISH_STOCK) return null
  return parsed
}

const trimmedOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Synthesize `FishStockLog` events from CSV-imported production rows.
 *
 * Used by the production-summary import path in `ImportWorkspace` to
 * dual-write imported `row.fishStock` values as authoritative FishStockLog
 * events (T3-A-04). The existing `ProductionRecord.fishStock` write remains
 * in place during the transition; T3-A-07 retires that column and drops
 * the direct write, leaving this synthesis as the only source of truth.
 *
 * Skip rules (produce no event for a row):
 * - `row.fishStock` is not a string, is blank, non-numeric, non-integer,
 *   negative, or greater than 10,000,000.
 * - `row.date` cannot be normalized to an ISO Z instant.
 * - The synthesis context is missing `farmId` or `productionUnitId`.
 *
 * The caller is responsible for filtering `rows` down to *imported* rows
 * only (e.g. via `savePlan.syncRowIds`) — this helper does not distinguish
 * imported rows from pre-existing rows already in the table.
 */
export const synthesizeFishStockLogsFromImportedRows = (
  rows: BiomassDataRow[],
  context: FishStockSynthesisContext
): FishStockLog[] => {
  const farmId = typeof context.farmId === 'string' ? context.farmId.trim() : ''
  const productionUnitId = typeof context.productionUnitId === 'string'
    ? context.productionUnitId.trim()
    : ''
  if (!farmId || !productionUnitId) return []

  const importedAt = new Date().toISOString()
  const logs: FishStockLog[] = []

  for (const row of rows) {
    const countedStock = parseFishStockValue(row.fishStock)
    if (countedStock === null) continue

    const normalizedDate = normalizeToIsoZMidnight(typeof row.date === 'string' ? row.date : '')
    if (!normalizedDate.ok) continue

    const productionRowId = String(row.id)
    const lotId = trimmedOrUndefined(row.lotId)

    logs.push({
      id: createId('fsl'),
      farmId,
      productionUnitId,
      lot: trimmedOrUndefined(row.lot),
      lotId,
      productionRowId,
      recordedAt: normalizedDate.isoZ,
      countedStock,
      method: 'count',
      notes: trimmedOrUndefined(row.notes),
      metadata: {
        source: 'csvImport',
        productionRowId,
        lotId,
        importedAt
      }
    })
  }

  return logs
}
